/**
 * Auth Configuration
 * Centralized configuration for auth-api and Keycloak OIDC.
 */

// Auth API URL - points to the auth-api service
// In dev, uses same-origin proxy (/auth-api → localhost:8082/api/v1) so cookies work
export const AUTH_API_URL =
	process.env.NODE_ENV === "production"
		? process.env.VUE_APP_AUTH_API_URL || "/api/v1"
		: process.env.VUE_APP_AUTH_API_URL || "/auth-api";

// Auth API base path for auth endpoints
export const AUTH_API_BASE = `${AUTH_API_URL}/auth`;

// Keycloak OIDC configuration
export const KEYCLOAK_URL =
	process.env.VUE_APP_KEYCLOAK_URL || "http://localhost:8180";
export const KEYCLOAK_CLIENT_ID =
	process.env.VUE_APP_KEYCLOAK_CLIENT_ID || "fincom-frontend";

// Default realm used when realm resolution is unavailable (dev environments).
// Set VUE_APP_DEFAULT_REALM in .env.local / .env.production to override.
const DEFAULT_REALM = process.env.VUE_APP_DEFAULT_REALM || "fincom";

/**
 * Resolve the Keycloak realm for a given email via auth-api.
 * Falls back to DEFAULT_REALM when the resolver is unavailable or not configured.
 * Returns { realm, subdomain } (subdomain is null when none is configured).
 */
export const resolveRealm = async (email) => {
	try {
		const res = await fetch(`${AUTH_API_URL}/realms/resolve`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ email }),
		});
		if (!res.ok) {
			const err = await res.json().catch(() => ({}));
			if (err.error === "service_unavailable") {
				console.warn(
					`Realm resolution not configured, using default realm '${DEFAULT_REALM}'`,
				);
				return { realm: DEFAULT_REALM, subdomain: null };
			}
			throw new Error(
				err.message || "Could not find an account for this email",
			);
		}
		const data = await res.json();
		if (!data.realm && !data.tenantId) {
			throw new Error("No realm configured for this account");
		}
		return {
			realm: data.realm || data.tenantId,
			subdomain: data.subdomain || null,
		};
	} catch (err) {
		if (
			err.message?.includes("service_unavailable") ||
			err.message?.includes("Realm resolution is not configured")
		) {
			console.warn(
				`Realm resolution unavailable, using default realm '${DEFAULT_REALM}'`,
			);
			return { realm: DEFAULT_REALM, subdomain: null };
		}
		throw err;
	}
};

export const resolveRealmBySubdomain = async (subdomain) => {
	try {
		const res = await fetch(
			`${AUTH_API_URL}/realms/resolve-by-subdomain?subdomain=${encodeURIComponent(subdomain)}`,
		);
		if (!res.ok) return null;
		const data = await res.json();
		return data.realm || data.tenantId || null;
	} catch {
		return null;
	}
};

// localStorage key for a realm the user resolved manually (via email) when
// subdomain resolution was unavailable. Persisting it lets us skip the email
// prompt on subsequent visits and redirect straight to the tenant's realm.
// Deliberately survives logout (see authService.forceLogout / AUTH_LOGOUT) so a
// returning user on a non-subdomain host doesn't have to re-enter their email.
export const LOGIN_REALM_KEY = "fincom_login_realm";

/**
 * Read the remembered login realm. Returns { realm, email } or null.
 */
export const getStoredLoginRealm = () => {
	try {
		const raw = localStorage.getItem(LOGIN_REALM_KEY);
		if (!raw) return null;
		const parsed = JSON.parse(raw);
		return parsed?.realm
			? { realm: parsed.realm, email: parsed.email || null }
			: null;
	} catch {
		return null;
	}
};

/**
 * Remember the realm (and the email used to resolve it, for login_hint) so the
 * next visit can bypass the email prompt.
 */
export const setStoredLoginRealm = (realm, email) => {
	if (!realm) return;
	try {
		localStorage.setItem(
			LOGIN_REALM_KEY,
			JSON.stringify({ realm, email: email || null }),
		);
	} catch {
		// Ignore quota / disabled-storage errors — this is a best-effort cache.
	}
};

/**
 * Forget the remembered realm (used by the "different organization" escape hatch).
 */
export const clearStoredLoginRealm = () => {
	try {
		localStorage.removeItem(LOGIN_REALM_KEY);
	} catch {
		// noop
	}
};

export const extractSubdomain = () => {
	const hostname = window.location.hostname;
	if (!hostname.endsWith(".fincom.co")) return null;

	// hostname is e.g. "acme.dev.fincom.co" or "portal.qa.fincom.co"
	// Strip ".fincom.co" → "acme.dev" or "portal.qa"
	const withoutBase = hostname.slice(0, hostname.length - ".fincom.co".length);
	// First segment is the subdomain, rest is the environment
	const firstDot = withoutBase.indexOf(".");
	if (firstDot === -1) return null; // e.g. "dev.fincom.co" — no subdomain

	const sub = withoutBase.slice(0, firstDot);
	const skip = ["app", "www", "login", "auth-api"];
	if (skip.includes(sub)) return null;

	return sub;
};

export default {
	AUTH_API_URL,
	AUTH_API_BASE,
	KEYCLOAK_URL,
	KEYCLOAK_CLIENT_ID,
	resolveRealm,
	resolveRealmBySubdomain,
	extractSubdomain,
	LOGIN_REALM_KEY,
	getStoredLoginRealm,
	setStoredLoginRealm,
	clearStoredLoginRealm,
};
